/**
 * Our own date and time fields. They always show en-GB dates ("Thu 22 Oct 2026", typed as 22/10/2026) and the person's
 * 12/24-hour clock (default 24h), whatever the browser or OS locale. Values and change events look exactly like the
 * native inputs they replace (yyyy-MM-dd, HH:mm, yyyy-MM-ddTHH:mm via e.target.value), so callers don't change.
 * Keyboard: type freely (2230, 10pm, 22/10, tomorrow), ↑/↓ step 15 minutes or a day (Shift: an hour or a week),
 * Alt+↓ opens the picker. Touch: the picker buttons give a calendar and a scrollable list of times.
 */
import { forwardRef, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type FocusEvent, type InputHTMLAttributes, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { getClockPref } from '@/lib/clockPref';
import { addDays, addMinutes, inRange, parseDate, parseTime, showDate, showTime } from '@/lib/dateInput';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { kind: 'date' | 'time' | 'datetime-local'; fieldClass: string };

const TZ = 'Europe/London';
const fakeEvent = (value: string, name?: string) =>
  ({ target: { value, name: name ?? '' }, currentTarget: { value, name: name ?? '' } } as unknown as ChangeEvent<HTMLInputElement>);

function usePopover(open: boolean, close: () => void) {
  const anchor = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom > 320 || r.top < 320;
      setPos({ top: below ? r.bottom + 4 : Math.max(8, r.top - 324), left: Math.min(r.left, window.innerWidth - 296), width: r.width });
    };
    place();
    const out = (e: MouseEvent | TouchEvent) => { if (!anchor.current?.contains(e.target as Node) && !pop.current?.contains(e.target as Node)) close(); };
    // Esc closes just the picker, never the dialog it sits in.
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); close(); } };
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true); window.addEventListener('keydown', esc, true);
    document.addEventListener('mousedown', out); document.addEventListener('touchstart', out);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); window.removeEventListener('keydown', esc, true); document.removeEventListener('mousedown', out); document.removeEventListener('touchstart', out); };
  }, [open, close]);
  return { anchor, pop, pos };
}

/**
 * t176u: blur events carry the field's value in the same format as change events (yyyy-MM-dd, HH:mm, yyyy-MM-ddTHH:mm),
 * like the native inputs, not the text on screen ("Thu 22 Oct 2026"), so save-on-blur callers get a usable value.
 */
const blurEvent = (e: FocusEvent<HTMLElement>, value: string, name?: string) => {
  const t = { value, name: name ?? '' };
  return Object.assign(Object.create(Object.getPrototypeOf(e) as object) as object, e, { target: t, currentTarget: t }) as FocusEvent<HTMLInputElement>;
};

function useControlled(props: Props) {
  const [inner, setInner] = useState(String(props.defaultValue ?? ''));
  const controlled = props.value !== undefined;
  const value = controlled ? String(props.value ?? '') : inner;
  // The newest value, including one emitted in this event before the parent re-renders (blur right after a commit).
  const latest = useRef(value);
  latest.current = value;
  const emit = (v: string) => { const prev = latest.current; latest.current = v; if (!controlled) setInner(v); if (v !== prev) props.onChange?.(fakeEvent(v, props.name)); };
  return { value, emit, latest };
}

/* ---------------- Time ---------------- */

function TimeField(props: Props & { innerRef?: React.Ref<HTMLInputElement> }) {
  const { kind: _k, fieldClass, innerRef, value: _v, defaultValue: _d, onChange: _c, min, max, step, className, onBlur, onFocus, onKeyDown, ...rest } = props;
  const { value, emit, latest } = useControlled(props);
  const pref = getClockPref();
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [bad, setBad] = useState(false);
  const touch = touchDates();
  // Phone: tap opens the time list in 5-minute steps (no keyboard), like the date field opens its calendar.
  const stepMin = Math.max(1, Math.round(Number(step) / 60) || (touch ? 5 : 15));
  const listId = useId();
  const close = useMemo(() => () => setOpen(false), []);
  const { anchor, pop, pos } = usePopover(open, close);
  const shown = draft ?? (value ? showTime(value, pref) : '');
  const ok = (v: string) => inRange(v, min as string | undefined, max as string | undefined);
  const pick = (v: string) => { if (ok(v)) { emit(v); setBad(false); } };
  const slots = useMemo(() => Array.from({ length: Math.ceil(1440 / Math.max(stepMin, 5)) }, (_, i) => addMinutes('00:00', i * Math.max(stepMin, 5))), [stepMin]);
  useEffect(() => { if (open) pop.current?.querySelector('[aria-selected=true]')?.scrollIntoView({ block: 'center' }); }, [open, pop]);
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); setOpen(true); return; }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = addMinutes(value || '09:00', (e.key === 'ArrowUp' ? 1 : -1) * (e.shiftKey ? 60 : stepMin));
      pick(next); setDraft(null);
    } else if (e.key === 'Enter') { commit(); }
    else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
  };
  const commit = () => {
    if (draft === null) return;
    const t = draft.trim();
    if (!t) { emit(''); setBad(false); }
    else { const p = parseTime(t); if (p && ok(p)) { emit(p); setBad(false); } else setBad(true); }
    setDraft(null);
  };
  return (
    <div ref={anchor} className={clsx('relative', className)}>
      <input ref={innerRef} {...rest} type="text" inputMode={touch ? 'none' : 'text'} autoComplete="off" data-kind="time" value={shown}
        placeholder={rest.placeholder ?? (touch ? 'Pick a time' : pref === '12h' ? 'e.g. 9:00 AM' : 'e.g. 09:00')} aria-invalid={bad || undefined}
        readOnly={touch || rest.readOnly} aria-haspopup={touch ? 'listbox' : undefined} aria-expanded={touch ? open : undefined}
        onClick={e => { rest.onClick?.(e); if (touch && !rest.disabled) setOpen(o => !o); }}
        onFocus={e => { if (!touch) setDraft(shown); onFocus?.(e); }}
        onChange={e => { setDraft(e.target.value); const p = parseTime(e.target.value); if (p && ok(p)) { emit(p); setBad(false); } }}
        onBlur={e => { commit(); onBlur?.(blurEvent(e, latest.current, rest.name)); }} onKeyDown={key}
        className={clsx(fieldClass, 'pr-9 tabular-nums', bad && 'ring-2 ring-rose-500')} />
      <button type="button" tabIndex={-1} disabled={rest.disabled} aria-label="Choose a time" aria-expanded={open} aria-controls={listId}
        onClick={() => setOpen(o => !o)} className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-white">
        <Clock className="h-4 w-4" aria-hidden />
      </button>
      {open && pos && createPortal(
        <div ref={pop} id={listId} role="listbox" aria-label="Times" style={{ top: pos.top, left: pos.left, width: Math.max(pos.width, 140) }}
          className="fixed z-[100] max-h-64 overflow-y-auto rounded-lg bg-white p-1 shadow-xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700" data-testid="time-picker">
          {slots.filter(ok).map(s => (
            <button key={s} type="button" role="option" aria-selected={s === value} onClick={() => { pick(s); setDraft(null); setOpen(false); }}
              className={clsx('block w-full rounded px-3 py-2 text-left text-sm tabular-nums', s === value ? 'bg-accent font-semibold text-on-accent' : 'hover:bg-slate-100 dark:hover:bg-slate-800')}>
              {showTime(s, pref)}
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
}

/* ---------------- Date ---------------- */

/** The years the picker's year menu offers: 100 back (dates of birth) to 10 ahead, within min / max, always the shown one. */
export function calendarYears(shown: number, today: string, min?: string, max?: string): number[] {
  const now = Number(today.slice(0, 4));
  const lo = Math.max(min ? Number(min.slice(0, 4)) : now - 100, 1900); const hi = max ? Number(max.slice(0, 4)) : now + 10;
  const ys: number[] = []; for (let y = Math.min(lo, shown); y <= Math.max(hi, shown); y++) ys.push(y);
  return ys.reverse();
}

function Calendar({ value, min, max, onPick, onClose }: { value: string; min?: string; max?: string; onPick: (iso: string) => void; onClose: () => void }) {
  const today = DateTime.now().setZone(TZ).toISODate()!;
  const [focus, setFocus] = useState(value || today);
  const month = DateTime.fromISO(focus, { zone: TZ }).startOf('month');
  const start = month.minus({ days: month.weekday - 1 }); // Monday first
  const days = Array.from({ length: 42 }, (_, i) => start.plus({ days: i }).toISODate()!);
  const grid = useRef<HTMLDivElement>(null);
  useEffect(() => { grid.current?.querySelector<HTMLButtonElement>(`[data-day="${focus}"]`)?.focus(); }, [focus]);
  const key = (e: KeyboardEvent) => {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (moves[e.key] !== undefined) { e.preventDefault(); setFocus(f => addDays(f, moves[e.key])); }
    else if (e.key === 'PageUp' || e.key === 'PageDown') { e.preventDefault(); setFocus(f => DateTime.fromISO(f).plus({ months: e.key === 'PageUp' ? -1 : 1 }).toISODate()!); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  };
  return (
    <div onKeyDown={key} className="w-72 p-2" data-testid="date-picker">
      <div className="mb-1 flex items-center justify-between">
        <button type="button" aria-label="Previous month" className="rounded p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setFocus(month.minus({ months: 1 }).toISODate()!)}><ChevronLeft className="h-4 w-4" /></button>
        {/* t353u: month and year jump straight to a date months (or years) away, rather than tapping › again and again. */}
        <div className="flex items-center gap-1 text-sm font-semibold" aria-live="polite">
          <select aria-label="Month" data-testid="date-picker-month" value={month.month} onChange={e => setFocus(month.set({ month: Number(e.target.value) }).toISODate()!)}
            className="rounded bg-transparent py-0.5 pl-1 pr-0 font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">
            {Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{DateTime.fromObject({ month: i + 1 }).setLocale('en-GB').toFormat('LLLL')}</option>)}
          </select>
          <select aria-label="Year" data-testid="date-picker-year" value={month.year} onChange={e => setFocus(month.set({ year: Number(e.target.value) }).toISODate()!)}
            className="rounded bg-transparent py-0.5 pl-1 pr-0 font-semibold tabular-nums hover:bg-slate-100 dark:hover:bg-slate-800">
            {calendarYears(month.year, today, min, max).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button type="button" aria-label="Next month" className="rounded p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setFocus(month.plus({ months: 1 }).toISODate()!)}><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-slate-500">{['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => <div key={d} className="py-1">{d}</div>)}</div>
      <div ref={grid} role="grid" className="grid grid-cols-7 gap-0.5">
        {days.map(d => {
          const dt = DateTime.fromISO(d, { zone: TZ });
          const out = dt.month !== month.month;
          const allowed = inRange(d, min, max);
          return (
            <button key={d} type="button" data-day={d} disabled={!allowed} tabIndex={d === focus ? 0 : -1} aria-label={dt.setLocale('en-GB').toFormat('cccc d LLLL yyyy')} aria-pressed={d === value}
              onClick={() => onPick(d)}
              className={clsx('h-9 rounded text-sm tabular-nums disabled:opacity-30', d === value ? 'bg-accent font-semibold text-on-accent' : 'hover:bg-slate-100 dark:hover:bg-slate-800',
                out && d !== value && 'text-slate-400', d === today && d !== value && 'ring-1 ring-inset ring-slate-400')}>
              {dt.day}
            </button>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-xs">
        <button type="button" className="rounded px-2 py-1 font-medium underline" onClick={() => onPick(today)} disabled={!inRange(today, min, max)}>Today</button>
        <button type="button" className="rounded px-2 py-1" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/**
 * t183u: on the phone layout (MobileShell puts `mobile` on <html>) a date field is read-only and a tap opens the calendar,
 * so the on-screen keyboard never appears. It stays a labelled, focusable field: Enter, Space or Alt+↓ open the calendar.
 */
export const touchDates = () => typeof document !== 'undefined' && document.documentElement.classList.contains('mobile');

function DateField(props: Props & { innerRef?: React.Ref<HTMLInputElement> }) {
  const { kind: _k, fieldClass, innerRef, value: _v, defaultValue: _d, onChange: _c, min, max, step: _s, className, onBlur, onFocus, onKeyDown, ...rest } = props;
  const { value, emit, latest } = useControlled(props);
  const touch = touchDates();
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [bad, setBad] = useState(false);
  const close = useMemo(() => () => setOpen(false), []);
  const { anchor, pop, pos } = usePopover(open, close);
  const input = useRef<HTMLInputElement | null>(null);
  const ok = (v: string) => inRange(v, min as string | undefined, max as string | undefined);
  const shown = draft ?? (value ? showDate(value) : '');
  const commit = () => {
    if (draft === null) return;
    const t = draft.trim();
    if (!t) { emit(''); setBad(false); }
    else { const p = parseDate(t); if (p && ok(p)) { emit(p); setBad(false); } else setBad(true); }
    setDraft(null);
  };
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); setOpen(true); return; }
    if (touch && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); return; }
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && value) {
      e.preventDefault();
      const next = addDays(value, (e.key === 'ArrowUp' ? 1 : -1) * (e.shiftKey ? 7 : 1));
      if (ok(next)) emit(next);
      setDraft(showDate(next, true));
    } else if (e.key === 'Enter') commit();
  };
  /**
   * t176u: focusing the input runs onFocus synchronously, before the parent re-renders with the picked value, so it
   * used to seed the draft with the OLD date (or '' when there was none); the next blur then "committed" that draft and
   * emitted the old value, undoing the pick (instant-save fields saved the date, then null). Focus first, then set the
   * draft to the picked date, so a later blur commits what was picked.
   */
  const pickFromCalendar = (d: string) => {
    emit(d); setBad(false); setOpen(false);
    if (touch) {
      // Phone: never focus the text field after a pick (that opens the keyboard); blur it and tell save-on-blur callers.
      setDraft(null);
      const el = input.current;
      if (el && document.activeElement === el) el.blur();
      else if (el) onBlur?.({ target: { value: d, name: rest.name ?? '' }, currentTarget: { value: d, name: rest.name ?? '' }, type: 'blur' } as unknown as FocusEvent<HTMLInputElement>);
      return;
    }
    input.current?.focus();
    setDraft(document.activeElement === input.current ? showDate(d, true) : null);
  };
  const setRef = (el: HTMLInputElement | null) => {
    input.current = el;
    if (typeof innerRef === 'function') innerRef(el); else if (innerRef) (innerRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
  };
  return (
    <div ref={anchor} className={clsx('relative', className)}>
      <input ref={setRef} {...rest} type="text" autoComplete="off" data-kind="date" value={shown} placeholder={rest.placeholder ?? (touch ? 'Pick a date' : 'dd/mm/yyyy')} aria-invalid={bad || undefined}
        readOnly={touch || rest.readOnly} inputMode={touch ? 'none' : rest.inputMode} aria-haspopup={touch ? 'dialog' : undefined} aria-expanded={touch ? open : undefined}
        onClick={e => { rest.onClick?.(e); if (touch && !rest.disabled) setOpen(o => !o); }}
        onFocus={e => { if (!touch) setDraft(latest.current ? showDate(latest.current, true) : shown); onFocus?.(e); }}
        onChange={e => { setDraft(e.target.value); const p = /\d{4}|today|tomorrow|yesterday/i.test(e.target.value) ? parseDate(e.target.value) : null; if (p && ok(p)) { emit(p); setBad(false); } }}
        onBlur={e => { commit(); onBlur?.(blurEvent(e, latest.current, rest.name)); }} onKeyDown={key}
        className={clsx(fieldClass, 'pr-9 tabular-nums', bad && 'ring-2 ring-rose-500')} />
      <button type="button" tabIndex={-1} disabled={rest.disabled} aria-label="Choose a date" aria-expanded={open}
        onClick={() => setOpen(o => !o)} className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-white">
        <CalendarDays className="h-4 w-4" aria-hidden />
      </button>
      {open && pos && createPortal(
        <div ref={pop} role="dialog" aria-label="Choose a date" style={{ top: pos.top, left: pos.left }}
          className="fixed z-[100] rounded-lg bg-white shadow-xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <Calendar value={value} min={min as string | undefined} max={max as string | undefined}
            onPick={pickFromCalendar} onClose={() => { setOpen(false); if (!touch) input.current?.focus(); }} />
        </div>, document.body)}
    </div>
  );
}

/* ---------------- Date + time ---------------- */

function DateTimeField(props: Props & { innerRef?: React.Ref<HTMLInputElement> }) {
  const { value, emit, latest } = useControlled(props);
  const wrap = useRef<HTMLDivElement>(null);
  // Blur for the pair: only when focus leaves both halves (and isn't moving into one of their pickers).
  const groupBlur = (e: FocusEvent<HTMLDivElement>) => {
    const to = e.relatedTarget as Node | null;
    if (to && (wrap.current?.contains(to) || (to as Element).closest?.('[data-testid=date-picker],[data-testid=time-picker]'))) return;
    props.onBlur?.(blurEvent(e, latest.current, props.name));
  };
  const [d, t] = value ? value.split('T') : ['', ''];
  const tt = (t ?? '').slice(0, 5);
  const { min, max } = props;
  const [minD, minT] = String(min ?? '').split('T');
  const [maxD, maxT] = String(max ?? '').split('T');
  const base = { fieldClass: props.fieldClass, disabled: props.disabled, required: props.required };
  return (
    <div ref={wrap} onBlur={groupBlur} className={clsx('flex gap-2', props.className)} data-testid={props['data-testid' as keyof Props] as string | undefined}>
      <DateField {...base} kind="date" innerRef={props.innerRef} id={props.id} aria-label={props['aria-label'] ? `${props['aria-label']} (date)` : undefined}
        className="min-w-0 flex-[3]" value={d} min={minD || undefined} max={maxD || undefined}
        onChange={e => emit(e.target.value ? `${e.target.value}T${tt || '09:00'}` : '')} />
      <TimeField {...base} kind="time" aria-label={props['aria-label'] ? `${props['aria-label']} (time)` : 'Time'} className="min-w-0 flex-[2]" value={tt}
        min={d && d === minD ? minT : undefined} max={d && d === maxD ? maxT : undefined}
        onChange={e => emit(e.target.value ? `${d || DateTime.now().setZone(TZ).toISODate()}T${e.target.value}` : '')} />
    </div>
  );
}

export const DateTimeInput = forwardRef<HTMLInputElement, Props>((props, ref) =>
  props.kind === 'time' ? <TimeField {...props} innerRef={ref} /> : props.kind === 'date' ? <DateField {...props} innerRef={ref} /> : <DateTimeField {...props} innerRef={ref} />);
DateTimeInput.displayName = 'DateTimeInput';

/** A button (e.g. a date range label) that opens our en-GB calendar; for "jump to a date" where no text field is shown. */
export function DatePickerButton({ value, onPick, children, className, title = 'Pick a date', 'aria-label': ariaLabel = 'Jump to date', defaultOpen = false, testId = 'date-picker-button' }: {
  value: string; onPick: (iso: string) => void; children: React.ReactNode; className?: string; title?: string; 'aria-label'?: string;
  /** t291u: start with the calendar open (Jump to date from a menu). */
  defaultOpen?: boolean; testId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const close = useMemo(() => () => setOpen(false), []);
  const { anchor, pop, pos } = usePopover(open, close);
  return (
    <div ref={anchor} className="relative inline-flex">
      <button type="button" className={className} title={title} aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(o => !o)} data-testid={testId}>{children}</button>
      {open && pos && createPortal(
        <div ref={pop} role="dialog" aria-label="Choose a date" style={{ top: pos.top, left: pos.left }}
          className="fixed z-[100] rounded-lg bg-white shadow-xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <Calendar value={value} onPick={d => { onPick(d); setOpen(false); }} onClose={() => setOpen(false)} />
        </div>, document.body)}
    </div>
  );
}
